
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import jsPDF from "jspdf";

export const useVideoAnalysis = () => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const { toast } = useToast();

  const analyzeVideo = async (videoUrl: string) => {
    if (!videoUrl) return;
    
    setAnalyzing(true);
    setAnalysisResult(null);
    
    try {
      // Call the analysis function
      const { data, error } = await supabase.functions.invoke('analyze-video', {
        body: { videoUrl }
      });
      
      if (error) throw error;
      
      setAnalysisResult(data);
      toast({
        title: "Analysis complete",
        description: "Video analysis has been completed successfully.",
      });
      
      return data;
    } catch (error: any) {
      console.error("Error analyzing video:", error);
      toast({
        variant: "destructive",
        title: "Analysis failed",
        description: error.message || "An error occurred during video analysis. Please try again.",
      });
      return null;
    } finally {
      setAnalyzing(false);
    }
  };

  const downloadReport = (analysisData: any) => {
    if (!analysisData) return;
    
    try {
      // Create a new PDF document
      const pdf = new jsPDF();
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `analysis-report-${timestamp}.pdf`;
      
      // Add title
      pdf.setFontSize(18);
      pdf.text("Football Video Analysis Report", 20, 20);
      pdf.setFontSize(12);
      pdf.text(`Generated on: ${new Date().toLocaleString()}`, 20, 30);
      pdf.text(`Analyzed by: ${analysisData.analyzedBy || "NVIDIA USD Search API"}`, 20, 40);
      
      // Add a separator line
      pdf.line(20, 45, 190, 45);
      
      let yPos = 55;
      
      // Add Players section
      if (analysisData.results?.players) {
        const players = analysisData.results.players;
        pdf.setFontSize(16);
        pdf.text("Player Analysis", 20, yPos);
        yPos += 10;
        
        pdf.setFontSize(12);
        if (players.tracking) {
          pdf.text(`Players tracked: ${players.tracking.count || 0}`, 25, yPos);
          yPos += 7;
          pdf.text(`Formation: ${players.tracking.formation || "N/A"}`, 25, yPos);
          yPos += 7;
        }
        
        if (players.statistics?.passAccuracy) {
          pdf.text(`Pass Accuracy - Team 1: ${players.statistics.passAccuracy.team1 || "N/A"}`, 25, yPos);
          yPos += 7;
          pdf.text(`Pass Accuracy - Team 2: ${players.statistics.passAccuracy.team2 || "N/A"}`, 25, yPos);
          yPos += 7;
        }
        
        yPos += 5;
      }
      
      // Add Ball section
      if (analysisData.results?.ball && yPos < 250) {
        const ball = analysisData.results.ball;
        pdf.setFontSize(16);
        pdf.text("Ball Analysis", 20, yPos);
        yPos += 10;
        
        pdf.setFontSize(12);
        if (ball.trajectory?.possessionTime) {
          pdf.text(`Possession - Team 1: ${ball.trajectory.possessionTime.team1 || "N/A"}`, 25, yPos);
          yPos += 7;
          pdf.text(`Possession - Team 2: ${ball.trajectory.possessionTime.team2 || "N/A"}`, 25, yPos);
          yPos += 7;
        }
        
        if (ball.trajectory?.speedData) {
          pdf.text(`Average Ball Speed: ${ball.trajectory.speedData.average || 0} km/h`, 25, yPos);
          yPos += 7;
          pdf.text(`Maximum Ball Speed: ${ball.trajectory.speedData.max || 0} km/h`, 25, yPos);
          yPos += 7;
        }
        
        yPos += 5;
      }
      
      // Add new page if needed
      if (yPos > 250) {
        pdf.addPage();
        yPos = 20;
      }
      
      // Add Tactical Insights section
      if (analysisData.results?.tacticalInsights && yPos < 250) {
        const insights = analysisData.results.tacticalInsights;
        pdf.setFontSize(16);
        pdf.text("Tactical Insights", 20, yPos);
        yPos += 10;
        
        pdf.setFontSize(12);
        if (insights.counterAttacks) {
          pdf.text(`Counter Attacks - Team 1: ${insights.counterAttacks.team1 || 0}`, 25, yPos);
          yPos += 7;
          pdf.text(`Counter Attacks - Team 2: ${insights.counterAttacks.team2 || 0}`, 25, yPos);
          yPos += 7;
        }
        
        if (insights.aiRecommendations && insights.aiRecommendations.length > 0) {
          pdf.text("AI Recommendations:", 25, yPos);
          yPos += 7;
          
          insights.aiRecommendations.forEach((rec: string) => {
            if (yPos > 280) {
              pdf.addPage();
              yPos = 20;
            }
            pdf.text(`• ${rec}`, 30, yPos);
            yPos += 7;
          });
        }
      }
      
      // Save the PDF
      pdf.save(filename);
      
      toast({
        title: "Report downloaded",
        description: "Analysis report has been downloaded as PDF.",
      });
    } catch (error: any) {
      console.error("Error generating PDF:", error);
      toast({
        variant: "destructive",
        title: "Download failed",
        description: "Could not generate PDF. Downloading as JSON instead.",
      });
      
      // Fallback to JSON download
      const report = JSON.stringify(analysisData, null, 2);
      const blob = new Blob([report], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analysis-report-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  return {
    analyzing,
    analysisResult,
    setAnalysisResult,
    analyzeVideo,
    downloadReport
  };
};
